<script setup>
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import ContactAPI from 'dashboard/api/contacts';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contact: {
    type: Object,
    default: () => ({}),
  },
  identityVerified: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const { t } = useI18n();
const candidates = ref([]);
const isLoading = ref(false);
const hasError = ref(false);

const isVerifiedBusinessUser = computed(() => props.identityVerified);

const normalizeEmail = value => String(value || '').trim().toLowerCase();
const normalizePhone = value => String(value || '').replace(/[^\d+]/g, '');
const normalizeName = value => String(value || '').trim().toLocaleLowerCase();

const searchableValues = computed(() =>
  [
    {
      key: 'email',
      value: normalizeEmail(props.contact.email),
      normalize: normalizeEmail,
      label: t('CONVERSATION_SIDEBAR.RELATED_CANDIDATES.SAME_EMAIL'),
    },
    {
      key: 'phone_number',
      value: normalizePhone(props.contact.phone_number),
      normalize: normalizePhone,
      label: t('CONVERSATION_SIDEBAR.RELATED_CANDIDATES.SAME_PHONE'),
    },
    {
      key: 'name',
      value: normalizeName(props.contact.name),
      normalize: normalizeName,
      label: t('CONVERSATION_SIDEBAR.RELATED_CANDIDATES.SAME_NAME'),
    },
  ].filter(item => item.value && (item.key !== 'name' || item.value.length >= 2))
);

const fetchCandidates = async () => {
  candidates.value = [];
  hasError.value = false;
  if (
    !props.contact.id ||
    isVerifiedBusinessUser.value ||
    !searchableValues.value.length
  ) {
    return;
  }

  isLoading.value = true;
  try {
    const responses = await Promise.all(
      searchableValues.value.map(item =>
        ContactAPI.search(item.value, 1, 'name', '', {})
      )
    );
    const candidateMap = new Map();

    responses.forEach(response => {
      (response.data?.payload || []).forEach(candidate => {
        if (candidate.id === props.contact.id) return;

        const matches = searchableValues.value.filter(
          item => item.normalize(candidate[item.key]) === item.value
        );
        if (!matches.length) return;

        const existing = candidateMap.get(candidate.id);
        candidateMap.set(candidate.id, {
          ...candidate,
          relationLabels: [
            ...(existing?.relationLabels || []),
            ...matches.map(match => match.label),
          ].filter((label, index, labels) => labels.indexOf(label) === index),
        });
      });
    });

    candidates.value = [...candidateMap.values()].slice(0, 5);
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const openCandidate = candidate => {
  router.push({
    name: 'contacts_edit',
    params: {
      accountId: router.currentRoute.value.params.accountId,
      contactId: candidate.id,
    },
  });
};

watch(
  () => [
    props.contact.id,
    props.contact.name,
    props.contact.email,
    props.contact.phone_number,
    props.identityVerified,
  ],
  fetchCandidates,
  { immediate: true }
);
</script>

<template>
  <section
    v-if="!isVerifiedBusinessUser"
    class="border-t border-n-weak px-3 py-3"
  >
    <div class="mb-2 flex items-center gap-1.5">
      <span class="i-lucide-search size-3.5 text-n-amber-10" />
      <h4 class="text-xs font-medium text-n-slate-12">
        {{ t('CONVERSATION_SIDEBAR.RELATED_CANDIDATES.TITLE') }}
      </h4>
    </div>
    <p class="mb-2 text-xs leading-5 text-n-slate-10">
      {{ t('CONVERSATION_SIDEBAR.RELATED_CANDIDATES.DESCRIPTION') }}
    </p>

    <div v-if="isLoading" class="flex justify-center py-3">
      <Spinner />
    </div>
    <p v-else-if="hasError" class="text-xs text-n-ruby-10">
      {{ t('CONVERSATION_SIDEBAR.RELATED_CANDIDATES.ERROR') }}
    </p>
    <p v-else-if="!candidates.length" class="text-xs text-n-slate-10">
      {{ t('CONVERSATION_SIDEBAR.RELATED_CANDIDATES.EMPTY') }}
    </p>
    <div v-else class="flex flex-col gap-1.5">
      <button
        v-for="candidate in candidates"
        :key="candidate.id"
        type="button"
        class="flex w-full items-center justify-between gap-2 rounded-lg bg-n-alpha-2 px-2.5 py-2 text-left hover:bg-n-alpha-3"
        @click="openCandidate(candidate)"
      >
        <span class="min-w-0">
          <span class="block truncate text-xs font-medium text-n-slate-12">
            {{ candidate.name || candidate.email || candidate.phone_number }}
          </span>
          <span class="block truncate text-xs text-n-slate-10">
            {{ candidate.relationLabels.join('、') }}
          </span>
        </span>
        <span class="i-lucide-chevron-right size-4 shrink-0 text-n-slate-9" />
      </button>
    </div>
  </section>
</template>
